/** Observe an instrumented section's rendered expansion state.
 * @param {string} sectionId section reported by its owner
 * @return {boolean|null} null means the owner has no DOM marker
 */
export function observeSection(sectionId) {
	const roots = [...document.querySelectorAll('[data-agent-section-id]')]
		.filter((node) => node.getAttribute('data-agent-section-id') === sectionId);
	if (!roots.length) return null;
	return roots.some((node) => node.getAttribute('data-agent-section-open') === 'true' &&
		!node.closest('[hidden], [aria-hidden="true"]') &&
		window.getComputedStyle(node).display !== 'none' &&
		window.getComputedStyle(node).visibility !== 'hidden' &&
		node.getClientRects().length > 0);
}

/**
 * Dispatch a resource event and wait for its mounted owner.
 * @param {string} eventName event identifier
 * @param {object} detail event payload
 * @param {number} timeoutMs acknowledgement timeout
 * @return {Promise<void>} completion acknowledgement
 */
export function dispatchAcknowledgedEvent(eventName, detail, timeoutMs = 5000) {
	return new Promise((resolve, reject) => {
		const timer = setTimeout(
			() => reject(new Error(`${eventName}-timeout`)),
			timeoutMs,
		);
		window.dispatchEvent(new CustomEvent(eventName, {
			detail: {
				...detail,
				// The page says what it actually reached, not merely that it
				// reached something.
				//
				// `complete()` resolved with nothing, so the backend said
				// "the SSID configuration is open" on the strength of a
				// component somewhere calling complete - which is evidence
				// that a listener ran, and not evidence about which screen
				// the user is looking at. A page that opened the wrong tab,
				// or the right form for the wrong record, acknowledged
				// identically to one that got it right.
				//
				// Optional on purpose: a caller that has nothing to add
				// still works, and reports nothing rather than something
				// invented.
				complete: (evidence) => {
					clearTimeout(timer);
					resolve(evidence && typeof evidence === 'object' ? evidence : {});
				},
				fail: (reason) => {
					clearTimeout(timer);
					reject(new Error(reason || `${eventName}-failed`));
				},
			},
		}));
	});
}

// How long a section has to report itself open.
//
// Longer than the five seconds every other command gets, because this is
// the one command that waits on a route change: the page it names is code
// split, so the chunk has to be fetched and the page mounted before anyone
// is listening at all. Global Profiles is the heaviest of those - measured
// at 4,881 ms from a cold navigate - which left the old five-second budget
// deciding the outcome by about a hundred milliseconds, and a section that
// had opened on screen still reported failure. Only the wait to be heard
// is longer; a section that answers `fail` still fails at once.
//
// Kept under the dispatcher's own 15s ceiling, which races every handler:
// reaching that first would report a generic command timeout instead of
// naming the section that never answered.
const SECTION_TIMEOUT_MS = 12000;

/**
 * Open a resource section, retrying until its lazy page mounts.
 * @param {string} eventName event identifier
 * @param {string} sectionId semantic section identifier
 * @param {Set<string>} allowedSections section allowlist
 * @param {number} timeoutMs acknowledgement timeout
 * @return {Promise<object>} what the page reports it opened
 */
export function openResourceSection(
	eventName,
	sectionId,
	allowedSections,
	timeoutMs = SECTION_TIMEOUT_MS,
) {
	if (!allowedSections.has(sectionId)) {
		return Promise.reject(new Error('unsupported_resource_section'));
	}
	return new Promise((resolve, reject) => {
		let settled = false;
		const finish = (callback) => {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			clearInterval(retryTimer);
			callback();
		};
		const detail = {
			sectionId,
			// See `dispatchAcknowledgedEvent` above: the page reports the
			// section it actually opened, so the reply can name it rather
			// than assume it.
			// Only what the page observed. `sectionId` is the command's own
			// parameter, and echoing it back proves nothing: a page that
			// never opened the section would report exactly the same thing
			// as one that did, which is evidence in shape and not in
			// substance. What the page saw is marked as observed; what it
			// was told is marked as asked, and the backend weighs them
			// differently.
			complete: (evidence) => {
				if (settled) return;
				const observed = evidence?.section_id ? observeSection(evidence.section_id) : null;
				// An instrumented component exists but has not painted its
				// expanded state yet. The bounded retry will check again.
				if (observed === false) return;
				finish(() => resolve(evidence && typeof evidence === 'object'
					? {...evidence, ...(observed === true ? {observed: true} : {}), asked_section_id: sectionId}
					: {asked_section_id: sectionId}));
			},
			fail: (reason) => finish(() => reject(new Error(reason || 'section_failed'))),
		};
		const dispatch = () => {
			if (settled) return;
			// Seeing it open is better evidence than being told, and it is the
			// only evidence available when the page is not painting.
			//
			// Every acknowledgement in the application is scheduled inside
			// `requestAnimationFrame`, and a browser suspends that in a hidden
			// tab. A user who switches away while io Genie works gets no
			// callback at all: the section opens, nothing reports it, and the
			// command times out with "Canvas could not open the Switch
			// inventory". Measured live - `document.hidden` true,
			// `requestAnimationFrame` never firing, the VLAN accordion marked
			// open in the DOM the whole time.
			//
			// So the retry that was already running now also looks. A section
			// that carries its marker and is open resolves on what the page
			// shows, whether or not anyone got round to saying so.
			if (observeSection(sectionId) === true) {
				finish(() => resolve({
					section_id: sectionId,
					asked_section_id: sectionId,
					observed: true,
				}));
				return;
			}
			window.dispatchEvent(new CustomEvent(eventName, {detail}));
		};
		const timer = setTimeout(
			() => finish(() => reject(new Error('section_timeout'))),
			timeoutMs,
		);
		const retryTimer = setInterval(dispatch, 100);
		dispatch();
	});
}
